import { type MouseEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { useBoolean } from 'usehooks-ts';

import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/dropdown';
import { DeleteTask } from '@/task-manager/api/mutations/delete-task';
import { SubscribeTask } from '@/task-manager/api/mutations/subscribe-task';
import { UnsubscribeTask } from '@/task-manager/api/mutations/unsubscribe-task';
import { UpdateTask } from '@/task-manager/api/mutations/update-task';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import { TaskStatus } from '@/task-manager/entities/task-status';
import { canSubmitResetRequest } from '@/task-manager/utils/safety-score';

import { AlertFeedbackDialog } from './alert-feedback-dialog';
import { GoToRecommendationsItem } from './go-to-recommendations-item';
import { MarkAsDoneButton } from './mark-as-done-button';
import { NotifyAccureDialog } from './notify-accure-dialog';
import {
  RequestResetDialog,
  type RequestResetDialogProps,
} from './request-reset-dialog';
import { SafetyScoreResetButton } from './safety-score-reset-button';
import { ShareTaskDialog } from './share-task-dialog';

export type TaskActionsProps = {
  task: TaskDetails;
  onClose?: () => void;
};
export function TaskActions({ task, onClose }: TaskActionsProps) {
  const subscribeTask = SubscribeTask.useMutation();
  const unsubscribeTask = UnsubscribeTask.useMutation();
  const updateTask = UpdateTask.useMutation();
  const deleteTask = DeleteTask.useMutation();
  const showNotifyDialog = useBoolean();
  const showShareDialog = useBoolean();
  const showFeedbackDialog = useBoolean();
  const [resetDialogProps, setResetDialogProps] = useState<
    Pick<RequestResetDialogProps, 'open' | 'cancelText'> | undefined
  >(undefined);

  return (
    <>
      <div className="flex flex-row justify-between gap-4 p-4">
        <div className="flex flex-row items-center gap-4">
          <SafetyScoreResetButton
            task={task}
            onClick={() => setResetDialogProps({ open: true })}
          />
          {task.status !== TaskStatus.DONE ? (
            <MarkAsDoneButton
              loading={updateTask.isLoading}
              onClick={() =>
                updateTask
                  .mutateAsync({
                    id: task.id,
                    status: TaskStatus.DONE,
                  })
                  .then(() => {
                    if (canSubmitResetRequest(task, TaskStatus.DONE)) {
                      showFeedbackDialog.setTrue();
                    } else {
                      onClose?.();
                    }
                  })
              }
            />
          ) : null}
        </div>
        <div className="flex flex-row items-center gap-4">
          <div className="relative">
            <Dropdown>
              <DropdownButton
                variant="ghost"
                className="p-1 text-zinc-600"
                data-testid="task-actions-button"
              >
                <EllipsisHorizontalIcon className="size-6" />
              </DropdownButton>
              <DropdownMenu
                className="absolute right-0 z-50"
                data-testid="task-actions-menu"
              >
                <GoToRecommendationsItem task={task} />
                {task.isSubscribed ? (
                  <DropdownItem
                    onClick={function (e: MouseEvent<HTMLDivElement>) {
                      e.preventDefault();
                      toast.promise(
                        unsubscribeTask.mutateAsync({ id: task.id }),
                        {
                          pending: 'Unfollowing ...',
                          success: 'You are no longer following this task',
                          error: 'Something went wrong',
                        },
                      );
                    }}
                  >
                    <div className="w-32">Unfollow this task</div>
                  </DropdownItem>
                ) : (
                  <DropdownItem
                    onClick={function (e: MouseEvent<HTMLDivElement>) {
                      e.preventDefault();

                      toast.promise(
                        subscribeTask.mutateAsync({ id: task.id }),
                        {
                          pending: 'Following ...',
                          success: 'You are now following this task',
                          error: 'Something went wrong',
                        },
                      );
                    }}
                  >
                    <div className="w-32">Follow this task</div>
                  </DropdownItem>
                )}
                <DropdownItem onClick={() => showShareDialog.setTrue()}>
                  Share task&hellip;
                </DropdownItem>
                <DropdownItem onClick={() => showNotifyDialog.setTrue()}>
                  Notify ACCURE&hellip;
                </DropdownItem>
                <DropdownItem
                  onClick={async function () {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this task?',
                      )
                    ) {
                      onClose?.();
                      await deleteTask.mutateAsync(task);
                    }
                  }}
                  className="text-red-600 ui-active:bg-red-50"
                >
                  Delete&hellip;
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>
      <NotifyAccureDialog
        className="mt-64"
        task={task}
        open={showNotifyDialog.value}
        onClose={() => showNotifyDialog.setFalse()}
      />
      <ShareTaskDialog
        task={task}
        open={showShareDialog.value}
        onClose={() => showShareDialog.setFalse()}
      />
      {showFeedbackDialog.value ? (
        <AlertFeedbackDialog
          task={task}
          newStatus={updateTask.isSuccess ? TaskStatus.DONE : task.status}
          open={true}
          onClose={function () {
            showFeedbackDialog.setFalse();
            setResetDialogProps({
              open: true,
              cancelText: 'Skip for now',
            });
          }}
        />
      ) : null}
      {resetDialogProps ? (
        <RequestResetDialog
          task={task}
          onClose={() => setResetDialogProps(undefined)}
          {...resetDialogProps}
        />
      ) : null}
    </>
  );
}
